import React from "react";
import {
  Check,
  Sparkles,
  Wrench,
  Target,
  Rocket,
  ArrowRight,
  Clock,
  Zap,
  Gauge,
} from "lucide-react";
import { handlePrimaryCta, PRIMARY_CTA_LABEL } from "@/lib/contact";
import useSnapCarousel from "@/hooks/useSnapCarousel";

const STARTUP_OPTIONS = [
  {
    id: "standard",
    icon: Clock,
    name: "Standard",
    price: 249,
    timeline: "~2 weeks",
    sub: "or sooner, as the job allows",
    accent: "border-nav-700 bg-nav-900/60",
    iconBg: "bg-nav-800 border-nav-700 text-white",
    featured: false,
  },
  {
    id: "fast-track",
    icon: Zap,
    name: "Fast-Track",
    price: 349,
    timeline: "Live in 7 days",
    sub: "priority queue & daily updates",
    accent: "border-brand bg-brand/10",
    iconBg: "bg-brand/20 border-brand/40 text-brand",
    featured: true,
  },
  {
    id: "same-day",
    icon: Gauge,
    name: "Same-Day Launch",
    price: 449,
    timeline: "Live in 24 hours",
    sub: "white-glove sprint, end-to-end",
    accent: "border-brand/50 bg-brand/5",
    iconBg: "bg-brand/15 border-brand/30 text-brand",
    featured: false,
  },
];

const PLANS = [
  {
    id: "tune-up",
    icon: Wrench,
    eyebrow: "Conversion Fix / Tune-Up",
    tagline:
      "Fix the leaks in your current site so more visitors actually book.",
    price: 249,
    bullets: [
      "Clear booking CTAs (book now, schedule a session)",
      "Mobile layout optimization",
      "Page simplification for better flow",
      "Basic booking & form tracking setup",
      "Monthly small updates (copy, layout, buttons)",
    ],
    accent: "border-nav-700",
    iconBg: "bg-nav-800 border-nav-700 text-white",
    featured: false,
  },
  {
    id: "lead-system",
    icon: Target,
    eyebrow: "Lead Optimization System",
    tagline:
      "Turn more of your existing traffic into booked appointments.",
    price: 349,
    bullets: [
      "Everything in Conversion Fix / Tune-Up",
      "Redesign of key pages (home + service pages)",
      "Stronger headlines and messaging",
      "Trust elements (testimonials, results, credibility)",
      "Sticky CTAs & integrated booking buttons",
      "Booking-flow optimization",
      "Monthly performance improvements",
    ],
    accent: "border-brand",
    iconBg: "bg-brand/20 border-brand/40 text-brand",
    featured: true,
    badge: "Most Popular",
  },
  {
    id: "call-engine",
    icon: Rocket,
    eyebrow: "Booking Engine",
    tagline:
      "Turn your website into a consistent source of inbound bookings.",
    price: 499,
    bullets: [
      "Everything in Lead Optimization System",
      "Full conversion-focused funnel optimization",
      "Dedicated landing pages (by service or offer)",
      "Ongoing A/B testing (headlines, CTAs, layouts)",
      "Advanced tracking (bookings, conversions)",
      "Speed optimization",
      "Priority updates and fast turnaround",
      "Monthly performance report (bookings, improvements, insights)",
    ],
    accent: "border-brand/40",
    iconBg: "bg-brand/15 border-brand/30 text-brand",
    featured: false,
  },
];

function PlanCard({ plan, innerRef }) {
  const Icon = plan.icon;
  return (
    <article
      ref={innerRef}
      data-testid={`pricing-plan-${plan.id}`}
      className={`relative rounded-3xl ${plan.featured ? "gradient-border-card" : `border-2 ${plan.accent}`} bg-nav-900/80 p-7 sm:p-8 flex flex-col h-full transition-all duration-300 hover:-translate-y-1
        ${plan.featured
          ? "lg:scale-[1.03] lg:z-10 shadow-[0_30px_60px_-20px_rgba(124,58,237,0.45)] hover:shadow-[0_36px_70px_-18px_rgba(236,72,153,0.45)]"
          : "hover:border-brand/50 hover:shadow-[0_24px_48px_-18px_rgba(91,91,255,0.35)]"}
      `}
    >
      {plan.badge && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
          <span className="gradient-badge inline-flex items-center gap-1.5 rounded-full text-white text-[10px] sm:text-xs font-bold px-3 sm:px-4 py-1.5 uppercase tracking-wider whitespace-nowrap">
            <Sparkles className="h-3 w-3" /> {plan.badge}
          </span>
        </div>
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-nav-400">
            Plan
          </p>
          <h3 className="mt-2 font-display font-bold text-2xl text-white">
            {plan.eyebrow}
          </h3>
        </div>
        <span className={`grid place-items-center h-11 w-11 rounded-xl border ${plan.iconBg}`}>
          <Icon className="h-5 w-5" />
        </span>
      </div>

      <p className="mt-4 text-sm text-nav-300 leading-relaxed">
        {plan.tagline}
      </p>

      {/* Monthly pricing — primary cost on this card */}
      <div className="mt-6 rounded-xl border border-brand/30 bg-brand/10 px-4 py-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand">
          Monthly
        </p>
        <div className="mt-1 flex items-baseline gap-2 flex-wrap">
          <span className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white leading-none">
            ${plan.price}
          </span>
          <span className="text-nav-200 text-sm font-semibold">/ month</span>
        </div>
      </div>

      <p className="mt-3 text-[11px] text-nav-500 leading-snug">
        + one-time startup fee · pick your launch speed below
      </p>

      <ul className="mt-5 space-y-2.5 flex-1">
        {plan.bullets.map((item) => (
          <li
            key={item}
            className="flex items-start gap-2 text-sm text-nav-200"
          >
            <span className="mt-0.5 grid place-items-center h-5 w-5 rounded-full bg-brand/15 text-brand border border-brand/20 shrink-0">
              <Check className="h-3.5 w-3.5" />
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <a
        href="#contact"
        onClick={handlePrimaryCta}
        data-testid={`pricing-cta-${plan.id}`}
        className={`mt-7 ${plan.featured ? "btn-primary" : "btn-secondary"} w-full`}
      >
        <Sparkles className="h-5 w-5" /> Get My Demo
      </a>
    </article>
  );
}

export default function Pricing() {
  const { scrollRef, setRef, active, scrollTo } = useSnapCarousel(
    PLANS.length,
    1 // mobile: start centered on "Most Popular"
  );

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="section bg-nav-950 relative overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[820px] rounded-full bg-brand/10 blur-3xl"
      />

      <div className="container-x relative">
        <div className="max-w-3xl mx-auto text-center">
          <span className="eyebrow justify-center">
            <span className="h-1.5 w-1.5 rounded-full bg-brand" />
            The Offer
          </span>
          <h2 className="mt-4 h-display text-4xl sm:text-5xl">
            See your booking funnel.{" "}
            <span className="text-brand">Then decide.</span>
          </h2>
          <p className="mt-4 text-lg text-nav-300">
            Choose the monthly plan that fits where your business is right now,
            plus a one-time launch fee based on how fast you want it live. We
            build first — you commit later.
          </p>
        </div>

        {/* ---------- MOBILE: horizontal swipeable carousel ---------- */}
        <div className="lg:hidden mt-14">
          <div
            ref={scrollRef}
            data-testid="pricing-track-mobile"
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide
              -mx-5 px-5 pt-6 pb-4"
            style={{ scrollPaddingLeft: "1.25rem", scrollPaddingRight: "1.25rem" }}
          >
            {PLANS.map((plan, i) => (
              <div
                key={plan.id}
                ref={setRef(i)}
                className="snap-center shrink-0 basis-[88%] sm:basis-[64%] md:basis-[52%] flex"
              >
                <PlanCard plan={plan} />
              </div>
            ))}
          </div>

          {/* Dot indicators */}
          <div
            className="mt-5 flex justify-center gap-2"
            data-testid="pricing-dots"
            role="tablist"
            aria-label="Pricing plans"
          >
            {PLANS.map((plan, i) => (
              <button
                key={plan.id}
                onClick={() => scrollTo(i)}
                aria-label={`Show ${plan.eyebrow}`}
                aria-selected={active === i}
                data-testid={`pricing-dot-${i}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  active === i
                    ? "w-7 bg-brand"
                    : "w-2 bg-nav-700 hover:bg-nav-600"
                }`}
              />
            ))}
          </div>
          <p className="mt-3 text-center text-xs text-nav-500">
            Swipe to compare — or tap a dot
          </p>
        </div>

        {/* ---------- DESKTOP: 3-col grid ---------- */}
        <div
          className="hidden lg:grid lg:grid-cols-3 gap-6 mt-14 pt-4"
          data-testid="pricing-grid-desktop"
        >
          {PLANS.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>

        {/* Startup Cost — 3 speed tiers */}
        <div className="mt-16 max-w-5xl mx-auto">
          <div className="text-center">
            <span className="eyebrow justify-center">
              <span className="h-1.5 w-1.5 rounded-full bg-brand" />
              Startup Cost · Pick Your Launch Speed
            </span>
            <h3 className="mt-4 h-display text-3xl sm:text-4xl">
              One-time setup.{" "}
              <span className="text-brand">Choose how fast you go live.</span>
            </h3>
            <p className="mt-3 text-sm sm:text-base text-nav-300 max-w-xl mx-auto">
              A one-time setup fee covers the full funnel build and integration
              with your booking tool. The faster you want to launch, the more
              concentrated our focus on your project.
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4 sm:gap-5">
            {STARTUP_OPTIONS.map((opt) => {
              const OptIcon = opt.icon;
              return (
                <div
                  key={opt.id}
                  data-testid={`startup-option-${opt.id}`}
                  className={`relative rounded-2xl ${opt.featured ? "gradient-border-card" : `border-2 ${opt.accent}`} bg-nav-900/60 p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                    opt.featured
                      ? "shadow-[0_20px_40px_-16px_rgba(124,58,237,0.4)] hover:shadow-[0_24px_50px_-14px_rgba(236,72,153,0.4)]"
                      : "hover:border-brand/50 hover:shadow-[0_18px_36px_-18px_rgba(91,91,255,0.3)]"
                  }`}
                >
                  {opt.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <span className="gradient-badge inline-flex items-center gap-1 rounded-full text-white text-[10px] font-bold px-3 py-1 uppercase tracking-wider whitespace-nowrap">
                        <Sparkles className="h-3 w-3" /> Most Popular
                      </span>
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-nav-400">
                        Launch speed
                      </p>
                      <h4 className="mt-1 font-display font-bold text-xl text-white">
                        {opt.name}
                      </h4>
                    </div>
                    <span className={`grid place-items-center h-10 w-10 rounded-lg border ${opt.iconBg}`}>
                      <OptIcon className="h-4 w-4" />
                    </span>
                  </div>
                  <div className="mt-5 flex items-baseline gap-2">
                    <span className="font-display text-4xl font-bold tracking-tight text-white leading-none">
                      ${opt.price}
                    </span>
                    <span className="text-nav-400 text-xs">one-time</span>
                  </div>
                  <div className="mt-3 inline-flex items-center gap-2 self-start rounded-full bg-nav-900/80 border border-nav-700 px-3 py-1.5">
                    <Clock className="h-3.5 w-3.5 text-brand" />
                    <span className="text-xs font-semibold text-nav-200">
                      {opt.timeline}
                    </span>
                  </div>
                  <p className="mt-3 text-xs text-nav-500 leading-snug">
                    {opt.sub}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <a
              href="#contact"
              onClick={handlePrimaryCta}
              data-testid="pricing-startup-cta"
              className="btn-primary"
            >
              {PRIMARY_CTA_LABEL}
              <ArrowRight className="h-5 w-5" />
            </a>
            <p className="mt-4 text-xs text-nav-500 leading-relaxed max-w-xl mx-auto">
              Not sure which plan + launch speed fits?{" "}
              <a
                href="#contact"
                onClick={handlePrimaryCta}
                className="text-brand font-semibold underline"
              >
                Send us a quick note
              </a>{" "}
              — we'll help you pick the right combination.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
